import { assignLocation, pushToHistory, replaceInHistory } from 'Core/actions/redirect';
import { skipRequest } from 'Core/actions/request';
import { setResponse } from 'Core/actions/response';
import { scrollToBase } from 'Core/actions/show';
import type { EpicArg } from 'Core/epics/common';
import { uriChangeHandled } from 'Core/history';
import {
  createFitmentSearchRequestSelectionSelector,
  createIsServerChangedVehicleSelector,
  isPageLoadedWithVehicleInUriSelector,
} from 'Core/selectors/fitmentSearch/index';
import {
  searchLastSearchOptionsSelector,
  searchRequestSelectionSelector,
  searchRequestSelector,
} from 'Core/selectors/search';
import { FacetValue, type SelectionEntry } from 'Models/index';
import { normalizePageSize, normalizeRequestPageSize } from 'Models/pagination';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { SearchRequestDefaults } from 'Models/uiConfig/requestTypes';
import uiConfig from 'Models/uiConfig/uiConfig';
import { getRequestFromUri, getUriFromRequest, getVehicleFromUri } from 'Modules/converter/index';
import { distinctSelection } from 'Modules/serverApi/common/convert';
import { SEARCH_RESPONSE_ERROR_STATES } from 'Modules/serverApi/types';
import { type CompareKey, equalByMany } from 'Utils/array';
import isTruthy from 'Utils/isTruthy';

import sendRequestWithPreselection from './sendRequest';
import tryAutocompleteRedirect, { shouldSearch } from './tryAutocompleteRedirect';

const scrollCompareKeys = ['sort', 'pageSize', 'pageSizeMode', 'pageNumber', requestConfig.pageUriParam];
const uriParamNames = ['filterQuery', 'catalog', 'query', ...scrollCompareKeys];
const uriDifferenceCompareKeys = [...uriParamNames, ['selection', FacetValue.termKey]];

const ignoredSearchParams = [
  ...uriParamNames.map((param) => param.toLowerCase()),
  'facet',
  'showed',
  ...(requestConfig.getIgnoredUriParamNames?.(uiConfig.pageType)?.map((param) => param.toLowerCase()) || []),
];
const defaultSearchValue =
  window.location.search
    ?.slice(1)
    .split('&')
    // without '=' check uri selection is treated as defaultSearch
    .filter((searchParam) => {
      if (searchParam.includes('=')) {
        const [paramName] = searchParam.split('=');
        return !ignoredSearchParams.includes(paramName.toLowerCase());
      }

      return false;
    })
    .join('&') || '';

export default function search(arg: EpicArg): void {
  const { state, action, dispatch } = arg;
  const sendRequest = requestConfig.isHtmxMode ? false : action.meta?.sendRequest;
  const onInit = action.meta?.onInit;

  if (state.search.retriesCount > 1) {
    return;
  }

  const result = tryAutocompleteRedirect(arg);
  result.actions.filter(isTruthy).forEach((nextAction) => dispatch(nextAction));
  if (
    result.shouldSearch === shouldSearch.forbid ||
    (result.shouldSearch === shouldSearch.allow && !sendRequest && action.type !== setResponse.type)
  ) {
    return;
  }
  // else search is forced

  const searchOptions = searchLastSearchOptionsSelector(state);
  const searchRequest = searchRequestSelector(state);
  const searchRequestSelection = searchRequestSelectionSelector(state);
  const fitmentSearchRequestSelection = createFitmentSearchRequestSelectionSelector()(state);

  const isVehicleInUriNew = getVehicleFromUri().isVehicleNew;
  const isPageLoadedWithVehicleInUri = isPageLoadedWithVehicleInUriSelector(state);

  const vehicleChangedByServer = createIsServerChangedVehicleSelector(null)(state);
  const showVehicleInUrl = !fitmentSearchConfig.hideVehicleFromUrl && !vehicleChangedByServer;

  const isVehicleSelectionShouldBeIncluded =
    (showVehicleInUrl || fitmentSearchConfig.doNotSaveSelectedVehicle) &&
    (requestConfig.hasSearchResults || isPageLoadedWithVehicleInUri) &&
    fitmentSearchConfig.isFitmentSearchWidgetsVisible &&
    !(onInit && isVehicleInUriNew);

  const combinedRequest = {
    ...searchRequest,
    selection: isVehicleSelectionShouldBeIncluded
      ? distinctSelection([...searchRequestSelection, ...fitmentSearchRequestSelection])
      : searchRequestSelection,
  };

  const uriRequest = normalizeRequestPageSize(
    getRequestFromUri(window.location),
    requestConfig.defaultPageSize,
  );
  const isRequestDiffersFromUri = requestDiffersFromUri(
    combinedRequest,
    uriRequest,
    uriDifferenceCompareKeys,
  );

  const { goToSearchPage, pathname, stayOnPage } = searchOptions ?? {};
  const uriOptions = { goToSearchPage, pathname, stayOnPage };
  const newLocation = getUriFromRequest(
    {
      ...combinedRequest,
      defaultSearch: defaultSearchValue,
      selection: toSelectionEntries(combinedRequest.selection),
    },
    uriOptions,
  );

  if (setResponse.match(action)) {
    if (SEARCH_RESPONSE_ERROR_STATES.some((errorState) => errorState === action.payload.State)) {
      return;
    }

    if (
      isRequestDiffersFromUri &&
      // if not on the category selection page. redirects are handled in the FacetTiles component
      !fitmentSearchConfig.isOnCategorySelectionPage &&
      !isVehicleInUriNew
    ) {
      dispatch(replaceInHistory(newLocation, null));
    }
    return;
  }

  if (searchOptions.fromUri) {
    if (onInit && isRequestDiffersFromUri) {
      dispatch(replaceInHistory(newLocation, null));
    }

    if (uiConfig.infiniteScrollEnabled) {
      const firstRequestForInfinite = { ...searchRequest };
      const pageSize =
        firstRequestForInfinite.pageSize ?? normalizePageSize(requestConfig.defaultPageSize).pageSize;

      firstRequestForInfinite.pageNumber = 0;
      firstRequestForInfinite.pageSize = pageSize * (searchRequest.pageNumber + 1);

      sendRequestWithPreselection(arg, firstRequestForInfinite);
    } else {
      sendRequestWithPreselection(arg, searchRequest);
    }
    return;
  }

  if (window.location.pathname !== newLocation.pathname) {
    const selection = toSelectionEntries(searchRequest.selection).concat(
      !searchOptions.ignoreLocalPreselection ? requestConfig.localPreselection : [],
    );

    const { href } = getUriFromRequest({ ...searchRequest, selection }, uriOptions);
    dispatch(assignLocation(href));
  } else if (isRequestDiffersFromUri) {
    dispatch(onInit ? replaceInHistory(newLocation, null) : pushToHistory(newLocation, uriChangeHandled));

    if (
      !uiConfig.disableRequestScroll ||
      requestDiffersFromUri(searchRequest, uriRequest, scrollCompareKeys)
    ) {
      dispatch(scrollToBase());
    }
    sendRequestWithPreselection(arg, searchRequest);
  } else if (searchOptions.allowSearchSameAsUri) {
    sendRequestWithPreselection(arg, searchRequest);
  } else {
    dispatch(skipRequest());
  }
}

function requestDiffersFromUri(
  request: SearchRequestDefaults,
  uriRequest: SearchRequestDefaults,
  compareKeys: Array<CompareKey<SearchRequestDefaults>>,
) {
  return !equalByMany(request, uriRequest, ...compareKeys);
}

function toSelectionEntries(selection: SelectionEntry[] | null | undefined): SelectionEntry[] {
  return selection ?? [];
}
